import { LabDocumentsTab } from '@web-app/components/CallV2/LabDocumentsTab/LabDocumentsTab';
import { PatientLabResults } from '@web-app/components/LabResults/PatientLabResults';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@web-app/components/ui/Tabs/tabs';
import { useInSessionLabResults } from '@web-app/hooks/lab-results/useInSessionLabResults';
import { cn } from '@web-app/lib/utils';
import { useState } from 'react';

type LabsView = 'results' | 'documents';

type Props = {
  patientId: string;
  isActive: boolean;
  showResults: boolean;
  showDocuments: boolean;
};

const VIEW_TAB_CLASS =
  'relative z-10 h-7 rounded-xl bg-transparent text-sm text-foreground shadow-none opacity-60 transition-opacity duration-200 data-[active]:bg-transparent data-[active]:opacity-100 data-[active]:shadow-none';

function LabResultsView({
  patientId,
  isActive,
}: {
  patientId: string;
  isActive: boolean;
}) {
  const { panels, isLoading } = useInSessionLabResults(patientId, isActive);

  return (
    <div
      className="h-full overflow-y-auto p-6 pt-4"
      data-testid="lab-results-view"
    >
      <PatientLabResults panels={panels} isLoading={isLoading} />
    </div>
  );
}

/** Structured lab results and uploaded lab documents for the patient, in one tab. */
export function LabsTab({
  patientId,
  isActive,
  showResults,
  showDocuments,
}: Props) {
  // Results is the default until the provider picks a view.
  const [chosenView, setView] = useState<LabsView | null>(null);
  const view = chosenView ?? 'results';

  if (!showDocuments) {
    return <LabResultsView patientId={patientId} isActive={isActive} />;
  }
  if (!showResults) {
    return <LabDocumentsTab />;
  }

  return (
    <Tabs
      value={view}
      onValueChange={(next) => setView(next as LabsView)}
      className="flex h-full min-w-0 flex-col"
    >
      <div className="px-3 pt-3">
        <TabsList className="relative isolate grid h-auto w-full grid-cols-2 rounded-2xl p-[3px]">
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-y-[3px] left-[3px] w-[calc((100%-6px)/2)] rounded-xl bg-background shadow-default-sm transition-transform duration-200 ease-out',
              view === 'documents' && 'translate-x-full',
            )}
          />
          <TabsTrigger value="results" className={VIEW_TAB_CLASS}>
            Results
          </TabsTrigger>
          <TabsTrigger value="documents" className={VIEW_TAB_CLASS}>
            Documents
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="results" className="min-h-0 flex-1">
        <LabResultsView
          patientId={patientId}
          isActive={isActive && view === 'results'}
        />
      </TabsContent>
      <TabsContent value="documents" className="min-h-0 flex-1 overflow-y-auto">
        <LabDocumentsTab />
      </TabsContent>
    </Tabs>
  );
}
